import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import { positionsResponseSchema, type AssignBody, type Position } from './types';

// Datos de los responsables por documento: alta y baja (refrescan la ficha, la Lista
// Maestra y el flujo, que muestran al responsable) y la búsqueda de puestos del LMS.

const BASE = '/api/v1/quality/master-list';

function useRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['dossier'] }),
      qc.invalidateQueries({ queryKey: ['master-list'] }),
      qc.invalidateQueries({ queryKey: ['flow-capabilities'] }),
    ]);
  };
}

export function useAssignResponsibility(
  entryId: string,
): UseMutationResult<void, Error, AssignBody> {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (body) => {
      await api.post(`${BASE}/${entryId}/responsibilities`, body);
    },
    // También tras un rechazo: otro usuario pudo cambiar la matriz.
    onSettled: refresh,
  });
}

export function useRemoveResponsibility(entryId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (responsibilityId) => {
      await api.delete(`${BASE}/${entryId}/responsibilities/${responsibilityId}`);
    },
    onSettled: refresh,
  });
}

export function usePositionSearch(q: string, enabled: boolean): UseQueryResult<Position[]> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['positions', activeCompanyId, q],
    queryFn: async () =>
      positionsResponseSchema.parse(
        await api.get<unknown>(`/api/v1/quality/positions?q=${encodeURIComponent(q)}`),
      ).data,
    enabled: enabled && Boolean(activeCompanyId),
    staleTime: 5 * 60 * 1000,
  });
}
